import { FilePlusIcon } from "./icons"
import { formatBytes } from "../../shared/services"
import { formatLabel } from "../services/formatLabel"

import type { Frame } from "../types/Frame"
import type { PreviewBackground } from "../types/PreviewBackground"
import type { Source } from "../types/Source"

const PREVIEW_BACKGROUNDS: { value: PreviewBackground; label: string; className: string }[] = [
  { value: "transparent", label: "Transparent", className: "bg-checkerboard" },
  { value: "light", label: "Light", className: "bg-base-100" },
  { value: "dark", label: "Dark", className: "bg-base-100" },
]

interface Props {
  source: Source
  // The frames as edited, which may no longer be the source's own.
  frames: Frame[]
  previewBackground: PreviewBackground
  isDone: boolean
  isConverting: boolean
  onPreviewBackgroundChange: (previewBackground: PreviewBackground) => void
  onNextFile: () => void
}

export const SourceBar = ({
  source,
  frames,
  previewBackground,
  isDone,
  isConverting,
  onPreviewBackgroundChange,
  onNextFile,
}: Props) => {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-3 max-sm:basis-full">
        <img
          src={frames[0].url}
          alt=""
          width={48}
          height={48}
          className="size-12 shrink-0 rounded-lg border border-base-content/10 object-contain bg-checkerboard"
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-bold" title={source.name}>
            {source.name}
          </span>
          <span className="text-xs text-muted-content">
            {formatLabel(source.type)} · {source.width}×{source.height} ·{" "}
            {formatBytes(source.bytes)}
            {frames.length > 1 ? ` · ${frames.length} frames → .ani` : " → .cur"}
          </span>
        </div>
      </div>

      <div
        role="group"
        aria-label="Background for the preview and the try zone"
        className="flex items-center gap-1.5 rounded-full bg-base-300 py-1 pr-1.5 pl-2.5 text-xs font-bold text-muted-content"
      >
        <span className="mr-1">Background</span>
        {PREVIEW_BACKGROUNDS.map(({ value, label, className }) => (
          <button
            key={value}
            type="button"
            data-theme={value === "transparent" ? undefined : value}
            aria-label={label}
            title={label}
            aria-pressed={previewBackground === value}
            onClick={() => onPreviewBackgroundChange(value)}
            className={`size-5.5 cursor-pointer rounded-full border-2 transition-transform hover:scale-110 ${className} ${
              previewBackground === value
                ? "border-primary ring-2 ring-primary/30"
                : "border-base-content/25"
            }`}
          />
        ))}
      </div>

      {/* aria-disabled, not disabled: a disabled button drops keyboard focus to <body>. */}
      <button
        type="button"
        aria-disabled={isConverting}
        onClick={isConverting ? undefined : onNextFile}
        className={`btn gap-2 rounded-xl font-bold max-sm:w-full ${
          isConverting ? "btn-disabled" : isDone ? "btn-primary" : "btn-neutral"
        }`}
      >
        <FilePlusIcon className="size-4" />
        Next file
      </button>
    </div>
  )
}
